import { FormattedMessage } from 'react-intl';
import { toastr } from 'react-redux-toastr';
import { Typography } from '@fortress-technology-solutions/fortress-component-library/Atoms';

import AffordableWaitlistActionModal from '../AffordableWaitlistActionModal';
import { useCancelFromWaitlist, useCancelKeepInProcess } from '../hooks';
import { cancelReasons, WAITLIST_ACTION_SCOPE } from '../constants';
import messages from '../messages';
import useIntl from '../../../hooks/useIntl';
import useReactHookFormProps from '../../../hooks/useReactHookFormProps';
import useHasPermission from '../../../hooks/useHasPermission';
import PermissionGatedActions from '../PermissionGatedActions';
import useSelectedProperty from '../../../hooks/useSelectedProperty';
import { useFetchWaitlistConfiguration } from '../../../hooks/data-fetching/useFetchWaitlistV2';
import { toAtomicProgramNames } from '../../../utils/affordable';
import {
  getShowContinueOption,
  getAvailablePrograms,
  resolveCancelMutate,
} from './utils';

const CancelFromWaitlist = ({
  organizationId,
  propertyId,
  affordableWaitlistId,
  open,
  handleClose,
  onSuccess,
}) => {
  const intl = useIntl();
  const selectedProperty = useSelectedProperty();
  const {
    data: waitlistConfiguration,
    isLoading: isLoadingWaitlistConfiguration,
  } = useFetchWaitlistConfiguration(propertyId, open);

  // The waitlist configuration only ever holds individual programs, so a
  // layered `LIHTC/HOME` record has to be split before either check below can
  // tell whether the waitlist already covers it.
  const propertyAffordablePrograms = toAtomicProgramNames(
    (selectedProperty?.pap ?? []).map(
      (map) => map.masterAffordableProgram?.name,
    ),
  );
  const showContinueOption =
    !isLoadingWaitlistConfiguration &&
    getShowContinueOption({
      propertyClassName: selectedProperty?.propertyClass?.name,
      propertyAffordablePrograms,
      waitlistConfiguration,
    });

  const availablePrograms =
    !isLoadingWaitlistConfiguration &&
    showContinueOption &&
    getAvailablePrograms({
      propertyClassName: selectedProperty?.propertyClass?.name,
      propertyAffordablePrograms,
      waitlistConfiguration,
    });

  const formatProgramList = (programs) => {
    if (!programs || programs.length === 0) return '';
    if (programs.length === 1) return programs[0];
    if (programs.length === 2) return `${programs[0]} and ${programs[1]}`;
    return `${programs.slice(0, -1).join(', ')}, and ${
      programs[programs.length - 1]
    }`;
  };

  // keptInProcess distinguishes the two cancel mutations for the caller: the
  // keep-in-process one only ends the waitlist record, so the application must
  // NOT be moved to Canceled.
  const buildMutationOptions = (keptInProcess) => ({
    onSuccess: () => {
      toastr.success('Success', intl.formatMessage(messages.cancelSuccess));
      // Deliberately no handleClose() here: handleClose means "the user
      // dismissed me", and firing it on success would clear the caller's
      // pending-action bookkeeping before onSuccess could read it — and a late
      // success from an already-dismissed modal would close whatever the user
      // has open now. Callers close the modal from onSuccess instead.
      onSuccess({ action: 'cancel', affordableWaitlistId, keptInProcess });
    },
    onError: (e) => toastr.error('Error', e?.message ?? e),
  });

  const { mutate: cancelMutate, isLoading: isCancelling } =
    useCancelFromWaitlist({
      organizationId,
      propertyId,
      options: buildMutationOptions(false),
    });
  const {
    mutate: cancelKeepInProcessMutate,
    isLoading: isCancellingKeepInProcess,
  } = useCancelKeepInProcess({
    organizationId,
    propertyId,
    options: buildMutationOptions(true),
  });

  const isSubmitting = isCancelling || isCancellingKeepInProcess;

  // A dismissed modal keeps its mutation in flight, so a caller can be handed a
  // success for an action the user already replaced. Block dismissal while the
  // request is pending; callers also verify the payload's action.
  const handleDismiss = () => {
    if (isSubmitting) return;
    handleClose();
  };

  const ReactHookFormProps = useReactHookFormProps({
    defaultValues: { reasons: [], note: '', continueApplication: null },
    mode: 'onChange',
  });
  const { getValues, isValid, isDirty } = ReactHookFormProps;

  const canAct = useHasPermission(WAITLIST_ACTION_SCOPE);

  const extraFields = [
    {
      variant: 'banner',
      status: 'info',
      message: <FormattedMessage {...messages.cancelBanner} />,
      GridProps: { xs: 12 },
    },
    {
      variant: 'formSubheading',
      children: <FormattedMessage {...messages.cancelDescriptionTitle} />,
      GridProps: { xs: 12, sx: { pb: 0 } },
    },
    {
      variant: 'custom',
      component: (
        <Typography variant="bodySmall" color="text.secondary">
          <FormattedMessage {...messages.cancelDescriptionHelperText} />
        </Typography>
      ),
      GridProps: { xs: 12, sx: { pt: 0 } },
    },
    ...(showContinueOption
      ? [
          {
            variant: 'radioGroup',
            name: 'continueApplication',
            label: (
              <FormattedMessage
                {...messages.continueApplicationLabel}
                values={{
                  programs: formatProgramList(availablePrograms),
                }}
              />
            ),
            options: [
              { value: 'true', label: <FormattedMessage {...messages.yes} /> },
              { value: 'false', label: <FormattedMessage {...messages.no} /> },
            ],
            rules: { required: true },
            sx: { flexDirection: 'row' },
            GridProps: { xs: 12, sx: { pl: 6, pr: 6 } },
          },
        ]
      : []),
  ];

  const actionsProps = [
    {
      children: <FormattedMessage {...messages.dismiss} />,
      onClick: handleDismiss,
      disabled: isSubmitting,
    },
    {
      children: <FormattedMessage {...messages.confirm} />,
      submitButton: true,
      isSubmitting,
      disabled: !isValid || !isDirty,
      onClick: () => {
        const values = getValues();
        const mutate = resolveCancelMutate(
          values.continueApplication === 'true',
          {
            cancelMutate,
            cancelKeepInProcessMutate,
          },
        );
        mutate({
          payload: {
            reasons: values.reasons,
            description: values.note ?? '',
          },
          affordableWaitlistId,
        });
      },
    },
  ];

  const modalProps = {
    title: <FormattedMessage {...messages.cancelTitle} />,
    ContentProps: { sx: { width: { xs: 'calc(100% - 32px)', sm: 680 } } },
    actionsProps,
    // The endpoint refuses this without the scope, so the footer says so
    // rather than letting the agent submit into a 403.
    ...(canAct
      ? {}
      : {
          actions: (
            <PermissionGatedActions
              actionsProps={actionsProps}
              tooltip={<FormattedMessage {...messages.permissionRequired} />}
            />
          ),
        }),
  };

  return (
    <AffordableWaitlistActionModal
      open={open}
      handleClose={handleDismiss}
      modalProps={modalProps}
      ReactHookFormProps={ReactHookFormProps}
      reasons={cancelReasons}
      extraFields={extraFields}
    />
  );
};

export default CancelFromWaitlist;
